import * as React from 'react';
import ReactCrop, {
  Crop,
  centerCrop,
  makeAspectCrop
} from 'react-image-crop';

import ActionButton from 'components/buttons/action-button/ActionButton';
import Spinner from 'components/spinner/Spinner';

import { __t, applyTranslations } from 'utils/language';
import { getCroppedImage } from 'utils/cropping';

import './crop-image-modal.scss';
import 'react-image-crop/dist/ReactCrop.css';

applyTranslations(require('./crop-image-modal.i18n.json'));

interface ImageStyle {
  maxHeight?: number,
  maxWidth?: number,
  height?: number,
  width?: number
}

type OwnProps = {
  images: Array<File>,
  uploadButtonLabel?: string,
  minWidth: number,
  minHeight: number,
  fixedDesktopRatio?: number,
  onUploadFiles: (file: Blob) => Promise<boolean | null>,
  onCloseModal: () => void
};

type Props = OwnProps;

const FIX_DESKTOP_RATIO = 2 / 5;

function hideSpinner() {
  const el = document.querySelector('.crop-image-modal__image-container-spinner');
  if (el === null) {
    return;
  }
  el.classList.add('crop-image-modal__image-container-spinner--fade-out');
}

function showSpinner() {
  const el = document.querySelector('.crop-image-modal__image-container-spinner');
  if (el === null) {
    return;
  }
  el.classList.remove('crop-image-modal__image-container-spinner--fade-out');
}

const CropImageModal: React.FC<Props> = ({
  images,
  uploadButtonLabel,
  minWidth,
  minHeight,
  fixedDesktopRatio = FIX_DESKTOP_RATIO,
  onUploadFiles,
  onCloseModal
}: Props): JSX.Element => {
  const reader = new FileReader();
  const [currentImagePostion, setCurrentImagePosition] = React.useState(0);
  const [currentImageSrc, setCurrentImageSrc] = React.useState<string>('');
  const [crop, setCrop] = React.useState<Crop>();
  const [style, setStyle] = React.useState<ImageStyle>({
    width: minWidth * fixedDesktopRatio,
    height: minHeight * fixedDesktopRatio
  });
  const [isUploading, setIsUploading] = React.useState(false);
  const [isInvalidImage, setIsInvalidImage] = React.useState(false);

  const imgRef = React.useRef<HTMLImageElement>(null);

  const centerAspectCrop = (
    width: number,
    height: number,
    isInvalid: boolean
  ) => {
    if (isInvalid) {
      return undefined;
    }

    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: (minWidth / width * 100) + 1
        },
        minWidth / minHeight,
        width,
        height
      ),
      width,
      height
    );
  };

  const loadImage = () => {
    reader.addEventListener('load', () => {
      const image = new Image();

      setCurrentImageSrc(reader.result?.toString() || '');

      if (reader.result) {
        image.src = reader.result?.toString();
      }

      image.onload = () => {
        const isInvalidImg = (minWidth > image.width) || (minHeight > image.height);
        setIsInvalidImage(isInvalidImg);

        setCrop(centerAspectCrop(image.width, image.height, isInvalidImg));
      };
    });
    reader.readAsDataURL(images[currentImagePostion]);
  };

  React.useEffect(() => {
    loadImage();

    return () => {
      reader.abort();
    };
  }, [currentImagePostion]);

  const uploadImage = async () => {
    setIsUploading(true);

    const blob = await getCroppedImage(imgRef, crop);

    const hasUploaded = await onUploadFiles(blob);

    setIsUploading(false);

    if (hasUploaded) {
      if (images[currentImagePostion + 1]) {
        setCurrentImagePosition(currentImagePostion + 1);
        showSpinner();
      } else {
        onCloseModal();
      }
    }
  };

  const [name, extension] = images[currentImagePostion].name.split('.');

  return (
    <div className="crop-image-modal">
      <div className="crop-image-modal__image-container">
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          aspect={minWidth / minHeight}
          onComplete={(c) => setCrop(c)}
        >
          <img
            ref={imgRef}
            alt="Crop me"
            src={currentImageSrc}
            className="crop-image-modal__image"
            style={style}
            onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
              const { width, height } = e.currentTarget;

              if (width / height > (minWidth / minHeight)) {
                setStyle({
                  maxWidth: minWidth * fixedDesktopRatio
                });
              } else {
                setStyle({
                  maxHeight: minHeight * fixedDesktopRatio
                });
              }

              hideSpinner();
            }}
          />
          <Spinner
            className="crop-image-modal__image-container-spinner"
            size="tiny"
            position="inline"
          />
        </ReactCrop>
      </div>
      <div className="crop-image-modal__action-container">
        <div className="crop-image-modal__name-with-extension">
          <div className="crop-image-modal__name text-overflow-ellipsis">
            {name}
          </div>
          {`.${extension}`}
        </div>
        <div className="crop-image-modal__count">
          {`${currentImagePostion + 1} / ${images.length}`}
        </div>
        <ActionButton
          color="primary"
          onClick={() => uploadImage()}
          disabledWithSpinner={!isInvalidImage}
          disabled={isUploading || isInvalidImage}
        >
          {uploadButtonLabel || __t('crop_image_modal.upload_btn')}
        </ActionButton>
        {
          isInvalidImage && (
            <React.Fragment>
              <ActionButton
                color="secondary"
                className="crop-image-modal__skip-btn"
                onClick={() => {
                  if (images[currentImagePostion + 1]) {
                    setCurrentImagePosition(currentImagePostion + 1);
                  } else {
                    onCloseModal();
                  }
                }}
              >
                {__t('crop_image_modal.skip_btn')}
              </ActionButton>
              <div className="crop-image-modal__error">
                {__t('realm_outfit_review.validation.dimension')}
              </div>
            </React.Fragment>
          )
        }
      </div>
    </div>
  );
};

export default CropImageModal;
